import isEmpty from "lodash/isEmpty";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import {
  useGetIngredientPackaging,
  useGetIngredientsSupplierList,
  usePutIngredientDataToQueue,
  useUploadIngredientImage
} from "../../../../api/ingredients";
import { useGetCountries, useGetProduceTypes, useGetSpecifyIngredient } from "../../../../api/static-data";
import { IngredientDataType } from "../../../../config/enums/IngredientType.enum";
import { MappingSourceType } from "../../../../config/enums/MappingSourceType.enum";
import { SupplierType } from "../../../../config/enums/SupplierType.enum";
import { errorView } from "../../../../helpers/ErrorHandler";
import { getTenantId } from "../../../../utils/cacheStorage";
import { useSupplierDataType } from "../../../ingredients/ingredientRegister/hooks/useSupplierDataType";
import { useSupplierFieldState } from "../../../ingredients/ingredientRegister/hooks/useSupplierFieldState";
import { useSupplierRegisterForm } from "../../../ingredients/ingredientRegister/hooks/useSupplierRegisterForm";
import { useSupplierSalesUnitSync } from "../../../ingredients/ingredientRegister/hooks/useSupplierSalesUnitSync";
import { useSupplierSave } from "../../../ingredients/ingredientRegister/hooks/useSupplierSave";
import { useSupplierSelection } from "../../../ingredients/ingredientRegister/hooks/useSupplierSelection";
import { SupplierIngredientLcaDataViewRef } from "../../../ingredients/shared/components/SupplierIngredientLcaDataView";
import { SupplierDataShape } from "../../../ingredients/ingredientRegister/supplierRegister.types";
import { derivePackageFlags, getIngredientId, getSupplierId } from "../../../ingredients/ingredientRegister/supplierRegister.utils";
import { IngredientRegisterButtonState } from "../services/IngredientRegisterButtonState";
import { IngredientRegisterOptions } from "../services/IngredientRegisterOptions";
import { IngredientRegisterViewModel, IngredientSelectionData } from "../types/ingredientRegisterView.types";
import { useIngredientNameSelection } from "./useIngredientNameSelection";

interface UseIngredientRegisterViewModelParams {
  visibleModal: boolean;
  setVisibleModal: (value: boolean) => void;
  refetchIngredients?: () => void;
}

/**
 * Owns every piece of state, data fetching and mutation handling for the
 * Tailwind register form — the register-flow counterpart to
 * `useIngredientListViewModel`. Presentational components under
 * `components/` consume the flat `IngredientRegisterViewModel` this hook
 * returns instead of touching the legacy hooks/API themselves.
 *
 * Calls the 6 legacy hooks (`useSupplierFieldState`, `useSupplierRegisterForm`,
 * `useSupplierSalesUnitSync`, `useSupplierDataType`, `useSupplierSelection`,
 * `useSupplierSave` — all from src/screens/ingredients/ingredientRegister/hooks/)
 * DIRECTLY rather than through the legacy `SupplierRegister.tsx` wrapper, so
 * the existing-supplier branch can render new Tailwind markup instead of the
 * Semantic UI `SupplierIngredientDataView`. `SupplierRegister.tsx` itself is
 * untouched and keeps serving the edit flow (`IngredientEditModal`) exactly
 * as before — each hook only holds internal `useState`/`useEffect` closure
 * state, so calling them from a second, independent site is safe by
 * construction.
 */
export const useIngredientRegisterViewModel = ({
  visibleModal,
  setVisibleModal,
  refetchIngredients
}: UseIngredientRegisterViewModelParams): IngredientRegisterViewModel => {
  const navigation = useNavigate();
  const lcaRef = useRef<SupplierIngredientLcaDataViewRef>(null);

  const [hideIngredientView, setHideIngredientView] = useState(false);
  // Only the setter is used directly — it's required as a callback target by
  // `useSupplierFieldState`/`useSupplierDataType` below, but reading this
  // state back would re-expose the exact hydration race `lockedDataType`
  // (below) guards against, since it mirrors `effectiveDataType` via those
  // hooks' own internal effect.
  const [, setCurrentDataType] = useState<string>(IngredientDataType.SUPPLIER);
  const [viewEnvironmentalData, setViewEnvironmentalData] = useState(false);
  const [currentSupplierId, setCurrentSupplierId] = useState<unknown>(null);
  const [loadPackageDataFlag, setLoadPackageDataFlag] = useState(false);
  const [validateData, setValidateData] = useState({
    country: 1,
    specifyIngredientValue: "",
    produceType: "",
    ingredientType: ""
  });
  const [calculateModalOpen, setCalculateModalOpen] = useState(false);
  const [supplierValidation, setSupplierValidation] = useState(false);
  const [isSupplierFormOpen, setIsSupplierFormOpen] = useState(false);
  const [supplierData, setSupplierData] = useState<SupplierDataShape>({});

  const ingredientNameSelection = useIngredientNameSelection();

  // Dedicated tiny form powering Card 1's two `FormSearchSelect`s
  // (src/components/ui/Input/FormSearchSelect/FormSearchSelect.tsx) — it
  // needs a real `control`+`name` field to bind to, and neither ingredient
  // nor supplier search values belong on the main `useSupplierRegisterForm`
  // instance (that form gets `reset()` on every ingredient change).
  const {
    control: searchControl,
    setValue: setSearchValue,
    reset: resetSearchForm,
    formState: { errors: searchErrors }
  } = useForm<Record<string, unknown>>();

  const ingredientId = getIngredientId(
    ingredientNameSelection.ingredientData as unknown as Record<string, unknown>
  );
  const supplierId = getSupplierId(supplierData);

  const supplierAsUser = Boolean(
    (ingredientNameSelection.ingredientData as Record<string, unknown> | undefined)?.supplierAsUser ||
      supplierData?.supplier_data?.is_supplier_as_user
  );

  // Identifies the current ingredient+supplier pairing for the "keep the
  // first hydrated value" guards below — see their comments.
  const registerPairKey = `${ingredientId || ""}:${supplierId || ""}`;

  const { data: ingredientSupplierList, refetch: refetchSupplierOptions } =
    useGetIngredientsSupplierList();
  const {
    data: countriesData,
    isSuccess: isCountriesDataSuccess,
    isLoading: isCountriesDataLoading
  } = useGetCountries();
  const {
    data: produceTypesData,
    isSuccess: isProduceTypesSuccess,
    isLoading: isProduceTypesLoading
  } = useGetProduceTypes();
  const { data: specifyIngredientData } = useGetSpecifyIngredient();

  const { data: ingredientPackageData, refetch: refetchPackagingData } = useGetIngredientPackaging({
    ingredientId,
    supplierId,
    loadPackageData: loadPackageDataFlag
  });

  const { mutate: uploadIngredientImage } = useUploadIngredientImage();

  const saveIngredientImage = useCallback(
    (data: unknown) => {
      if (ingredientNameSelection.imageData) {
        uploadIngredientImage({
          ingredient_id: ((data as Record<string, unknown>)?.ingredient_data as Record<string, unknown>)
            ?._id,
          file: ingredientNameSelection.imageData
        });
      }
    },
    [ingredientNameSelection.imageData, uploadIngredientImage]
  );

  const {
    newSupplier,
    setNewSupplier,
    ingredientType,
    setIngredientType,
    defaultType,
    setDefaultType,
    country,
    setCountry,
    supplierCountry,
    setSupplierCountry,
    isSupplierAsUser,
    setIsSupplierAsUser,
    produceType,
    setProduceType,
    farmingType,
    setFarmingType,
    fieldType,
    setFieldType,
    specifyIngredientValue,
    setSpecifyIngredientValue,
    isSupplierExist,
    setIsSupplierExist,
    resetFields,
    applyIngredientDetails
  } = useSupplierFieldState({
    currentDataType: setCurrentDataType,
    loadPackageData: setLoadPackageDataFlag,
    // `useGetCountries`'s declared `country` type ({key,text,value}) doesn't
    // match the raw API response it actually returns ({id,country,...}) —
    // same runtime shape the legacy `SupplerRegister.tsx` reads `.id` off of.
    getDefaultCountry: () => (countriesData as unknown as Array<{ id: number }> | undefined)?.[0]?.id
  });

  // `ingredientType` is hydrated from two independent sources for the same
  // ingredient+supplier pairing: immediately/synchronously when a supplier
  // already linked to this ingredient is picked (`useSupplierSelection`,
  // reading data already in the supplier list), and again — on its own,
  // network-dependent schedule — by `useSupplierSalesUnitSync`'s own live
  // fetch. When the second one resolves after the first and disagrees
  // (e.g. its response doesn't carry a specific ingredient type and the
  // hook falls back to its own default), it silently overwrites the
  // correct value. This locks onto whichever value arrives *first* for a
  // given pairing and ignores further auto-hydration for that same pairing
  // — while still always accepting the user's own edit via
  // `onIngredientTypeChange` below (flagged through the ref). The lock
  // resets the moment the pairing itself changes. Mutating a ref during
  // render like this is deliberate — it's the standard "derive a value
  // that depends on the previous render" pattern, not an effect, so
  // `useSupplierSave`/`packageFlags`/the mirrored form field below all see
  // the same corrected value in this same render, not one render later.
  const ingredientTypePairRef = useRef<string | null>(null);
  const isIngredientTypeUserEditRef = useRef(false);
  const lockedIngredientTypeRef = useRef(ingredientType);
  if (ingredientTypePairRef.current !== registerPairKey) {
    ingredientTypePairRef.current = registerPairKey;
    lockedIngredientTypeRef.current = ingredientType;
    isIngredientTypeUserEditRef.current = false;
  } else if (isIngredientTypeUserEditRef.current) {
    lockedIngredientTypeRef.current = ingredientType;
    isIngredientTypeUserEditRef.current = false;
  }
  const lockedIngredientType = lockedIngredientTypeRef.current;

  const {
    register,
    reset,
    handleSubmit,
    setValue,
    control,
    errors,
    registerNewSupplier,
    resetNewSupplier,
    handleSubmitNewSupplier,
    controlNewSupplier,
    errorsNewSupplier
  } = useSupplierRegisterForm({
    newSupplier,
    country,
    specifyIngredientValue,
    produceType,
    ingredientType: lockedIngredientType,
    setSupplierValidation,
    setIsSupplierFormOpen,
    onChangeValidateData: setValidateData
  });

  const ingredientSuppliers = (
    (ingredientNameSelection.ingredientData as Record<string, unknown> | undefined)
      ?.ingredient_data as Record<string, unknown> | undefined
  )?.suppliers as Array<{ _id?: string }> | undefined;

  const { salesUnitData, isSalesUnitDataSuccess } = useSupplierSalesUnitSync({
    ingredientId,
    supplierId,
    isSupplierExist,
    setIsSupplierExist,
    ingredientSuppliers,
    selectedSupplierId: supplierData?.supplier_data?._id,
    applyIngredientDetails,
    setViewEnvironmentalData,
    setValue
  });

  const { effectiveDataType, setDataType } = useSupplierDataType({
    supplierData,
    setSupplierData,
    salesUnitData,
    supplierAsUser,
    currentDataType: setCurrentDataType
  });

  // Same "keep the first hydrated value for this pairing" guard as
  // `lockedIngredientType` above, for Data Type — `effectiveDataType` is
  // just as exposed to the same two-source race (it factors in
  // `salesUnitData` directly, see `useSupplierDataType`).
  const dataTypePairRef = useRef<string | null>(null);
  const isDataTypeUserEditRef = useRef(false);
  const lockedDataTypeRef = useRef(effectiveDataType);
  if (dataTypePairRef.current !== registerPairKey) {
    dataTypePairRef.current = registerPairKey;
    lockedDataTypeRef.current = effectiveDataType;
    isDataTypeUserEditRef.current = false;
  } else if (isDataTypeUserEditRef.current) {
    lockedDataTypeRef.current = effectiveDataType;
    isDataTypeUserEditRef.current = false;
  }
  const lockedDataType = lockedDataTypeRef.current;

  const { closeSupplierData, saveValues, getSearchValue } = useSupplierSelection({
      ingredientId,
      resetFields,
      applyIngredientDetails,
      setValue,
      setViewEnvironmentalData,
      setNewSupplier,
      setIsSupplierFormOpen,
      setIsSupplierAsUser,
      hideIngredientView: setHideIngredientView,
      resetNewSupplier,
      // No-op: with the 6 hooks called directly here (not through
      // SupplierRegister.tsx), there's no separate parent copy of
      // supplierData to mirror — setSupplierData below is the only copy —
      // matching IngredientRegisterModalView.tsx's own `() => {}` usage.
      supplierCurrentData: () => {},
      setSupplierData,
      loadPackageData: setLoadPackageDataFlag,
      checkCurrentSupplier: setCurrentSupplierId
    });

  // `value` is the selected option's id as a string (FormSearchSelect's
  // onChange). Reconstructs the `{result: {id, title, other: {supplierDetails}}}`
  // shape `useSupplierSelection.getSearchValue` (one of the 6 protected
  // hooks) expects, from the raw supplier list — `useGetIngredientsSupplierList`
  // always prepends real `{id: 0, title: "+ New"}`/`{id: 1, title: "In-house
  // supplier"}` entries (see src/api/ingredients.ts), so both special cases
  // are just regular, searchable options here, not synthetic no-results
  // fallbacks.
  const onSelectSupplier = useCallback(
    (value: string) => {
      const selected = (ingredientSupplierList as Array<Record<string, unknown>> | undefined)?.find(
        option => String(option.id) === value
      );
      if (!selected) return;

      getSearchValue({
        result: {
          id: selected.id,
          title: selected.title,
          other: { supplierDetails: selected.supplierDetails }
        }
      });
    },
    [ingredientSupplierList, getSearchValue]
  );

  // `ingredientSupplierList` only gains the just-created (or just-linked)
  // supplier once `refetchSupplier` — called inside `handleSupplierSave`'s
  // onSuccess, see useSupplierSave.ts — actually resolves, a real network
  // round trip. `supplier_search`'s field value is set to that supplier's id
  // immediately on save (`performSave` below), before that refetch
  // necessarily returns, and `FormSearchSelect` can only display a label for
  // a value it finds among `options` (`getDisplayValue` in
  // FormSearchSelect.tsx) — so without this, the field shows nothing at all
  // until the refetch happens to land. Synthesizing the currently-selected
  // `supplierData` as a fallback option guarantees a match always exists
  // immediately; once the real list refetches and already contains it, this
  // is skipped, so nothing is duplicated.
  const supplierOptions = useMemo(() => {
    const baseOptions = (ingredientSupplierList as Array<Record<string, unknown>> | undefined) || [];
    const currentSupplierId = supplierData?.supplier_data?._id;
    const alreadyListed = baseOptions.some(option => String(option.id) === String(currentSupplierId));

    if (!currentSupplierId || alreadyListed) {
      return baseOptions;
    }

    return [
      ...baseOptions,
      {
        id: currentSupplierId,
        title: supplierData?.supplier_name,
        supplierDetails: supplierData?.supplier_data
      }
    ];
  }, [ingredientSupplierList, supplierData]);

  const onCreateIngredient = useCallback(async () => {
    const createdId = await ingredientNameSelection.onCreateIngredient();
    if (createdId) setSearchValue("ingredient_search", createdId);
  }, [ingredientNameSelection, setSearchValue]);

  const { saveData, handleSupplierSave } = useSupplierSave({
    supplierData,
    ingredientData: ingredientNameSelection.ingredientData as unknown as Record<string, unknown>,
    effectiveDataType: lockedDataType,
    viewEnvironmentalData,
    lcaRef,
    ingredientType: lockedIngredientType,
    defaultType,
    country,
    specifyIngredientValue,
    produceType,
    farmingType,
    fieldType,
    supplierCountry,
    isSupplierAsUser,
    saveValues,
    refetchSupplier: refetchSupplierOptions,
    setIngredientData: data =>
      ingredientNameSelection.setIngredientData(data as IngredientSelectionData | undefined),
    saveIngredientImage,
    setSupplierData,
    supplierCurrentData: () => {},
    checkCurrentSupplier: setCurrentSupplierId,
    resetNewSupplier,
    setNewSupplier,
    setIsSupplierFormOpen,
    hideIngredientView: setHideIngredientView,
    setIsSupplierAsUser
  });

  const packageFlags = useMemo(
    () => derivePackageFlags(lockedDataType, lockedIngredientType),
    [lockedDataType, lockedIngredientType]
  );

  // `ingredientType`/`defaultType`/`country`/`produceType`/`farmingType`/
  // `fieldType`/`specifyIngredientValue`/`effectiveDataType` all live as
  // plain state in the legacy `useSupplierFieldState`/`useSupplierDataType`
  // hooks, not as fields on the RHF form `useSupplierRegisterForm` returns —
  // but `FormSelect` (src/components/ui/Input/FormSelector/FormSelect.tsx)
  // can only bind to a real `control`+`name` field. These mirror each piece
  // of state into a same-named field on that SAME form (auto-registered by
  // `FormSelect`'s own `Controller` on first render) purely so `FormSelect`
  // has something to read/display — the plain state above stays the actual
  // source of truth the legacy hooks/save logic use; `FormSelect`'s
  // `onChange` prop (below, in the returned view model) pushes edits back
  // out to that state, one-way, on top of this one-way-in mirror.
  //
  // `useLayoutEffect`, not `useEffect`: hydrating an existing supplier link
  // (`applyIngredientDetails`) sets this state in the SAME render that first
  // makes the Environmental Data card's fields visible (`viewEnvironmentalData`
  // flips true together with it) — a plain `useEffect` fires after paint, so
  // the freshly-mounted `FormSelect` briefly (or, on a slow render, visibly)
  // shows unset before the mirror catches up. `useLayoutEffect` flushes the
  // corrected value before the browser paints, so it never renders unset.
  useLayoutEffect(
    () => setValue("ingredient_type", lockedIngredientType),
    [setValue, lockedIngredientType]
  );
  useLayoutEffect(() => setValue("country", String(country)), [setValue, country]);
  useLayoutEffect(() => setValue("produce_type", produceType), [setValue, produceType]);
  useLayoutEffect(() => setValue("farming_type", farmingType), [setValue, farmingType]);
  useLayoutEffect(() => setValue("field_type", fieldType), [setValue, fieldType]);
  useLayoutEffect(
    () => setValue("specify_ingredient", specifyIngredientValue),
    [setValue, specifyIngredientValue]
  );
  useLayoutEffect(() => setValue("data_type", lockedDataType), [setValue, lockedDataType]);

  // Belt-and-braces on top of the mirror above: `useSupplierSalesUnitSync`
  // hydrates these same fields a *second* time from its own live fetch
  // (`useGetSalesUnitByIngredient`), which can resolve well after the fields
  // are already mounted — not a first-mount race, so `useLayoutEffect` alone
  // doesn't guarantee the Select's reactive subscription has actually picked
  // up the new value by the time it repaints. Forcing a clean remount (via
  // `key`) every time any mirrored value changes sidesteps that subscription
  // entirely — a freshly-mounted `Controller` reads straight from the form
  // store, which the effects above have, by construction, already written
  // to in this same commit (declared last, so it runs after every `setValue`
  // call above). This also remounts on a user's own edits, not just
  // hydration — harmless, since the value being displayed is already correct
  // at that point either way.
  const [formSyncKey, setFormSyncKey] = useState(0);
  // `UnitDetailsCard`/`EnvironmentalDataCard` only mount once `showUnitDetails`
  // (`!isEmpty(supplierData)`) flips true — a brand-new supplier link (no
  // existing `supplierIngredientDetails`) leaves `defaultType`/etc. unchanged
  // from their already-correct initial values, so none of the other deps
  // below change and the effect never reruns for that transition. Without a
  // remount here, the `unit_type` Select's very first mount has to rely on
  // the mirror `setValue` calls above having already reached the form store,
  // which isn't guaranteed for a field that didn't exist yet when they ran.
  const isSupplierDataEmpty = isEmpty(supplierData);
  useLayoutEffect(() => {
    setFormSyncKey(key => key + 1);
  }, [
    lockedIngredientType,
    defaultType,
    country,
    produceType,
    farmingType,
    fieldType,
    specifyIngredientValue,
    lockedDataType,
    isSupplierDataEmpty
  ]);

  // Reset supplier-side fields whenever the selected ingredient changes —
  // mirrors SupplierRegister.tsx's `useEffect(resetSupplierFields, [ingredientData])`
  // (its `loadSupplierEditData` half only applies when `disableEdit` is true,
  // which never happens on the register flow, so it's omitted here).
  useEffect(() => {
    reset();
    resetFields();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ingredientNameSelection.ingredientData]);

  // Mirrors IngredientRegisterModalView.tsx's effect enabling the
  // environmental-data section when the linked supplier already has one.
  useEffect(() => {
    const details = Array.isArray(supplierData?.supplier_data?.supplierIngredientDetails)
      ? (supplierData?.supplier_data?.supplierIngredientDetails as Array<Record<string, unknown>>).find(
          // eslint-disable-next-line eqeqeq -- loose match preserved from IngredientRegisterModalView.tsx (ObjectId vs string)
          (e: any) => e.ingredient_id == ingredientId
        )
      : (supplierData?.supplier_data?.supplierIngredientDetails as Record<string, unknown> | undefined);

    if ((details as Record<string, unknown> | undefined)?.data_type) {
      setViewEnvironmentalData(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supplierData]);

  // Reset everything when the modal closes — mirrors
  // IngredientRegisterModalView.tsx's close effect.
  useEffect(() => {
    if (!visibleModal) {
      ingredientNameSelection.resetIngredientSelection();
      resetSearchForm();
      setSupplierData({});
      setLoadPackageDataFlag(false);
      setHideIngredientView(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleModal]);

  const closeModal = useCallback(() => {
    refetchIngredients?.();
    if (hideIngredientView) {
      closeSupplierData();
    } else {
      setVisibleModal(false);
    }
  }, [refetchIngredients, hideIngredientView, closeSupplierData, setVisibleModal]);

  const performSave = useCallback(async () => {
    const documents = lcaRef.current?.getUploadedDocuments?.() || [];

    if (newSupplier) {
      await new Promise<void>((resolve, reject) => {
        handleSubmitNewSupplier((data: Record<string, unknown>) => {
          if (documents.length > 0) data.supportingDocuments = documents;
          // Backend's POST /supplier (create) requires `tenantId` — legacy's
          // `SupplierRegisterForm` never collected it either, so it must have
          // relied on something else supplying it; every other create-payload
          // call site in the app (e.g. NoteAndDocument.tsx) sets it explicitly
          // from `getTenantId()` (src/utils/cacheStorage.ts), so this does the
          // same rather than touching the protected `handleSupplierSave` hook.
          data.tenantId = getTenantId();
          // `handleSupplierSave`'s own `dataToSubmit` (useSupplierSave.ts)
          // never sets `supplier_type` for the create path — unlike its
          // sibling `saveData`, which always defaults to
          // `supplierData?.supplier_type || SupplierType.External`. A brand
          // new supplier created from this form is always an external one
          // (in-house suppliers are chosen from the list, never created
          // here), so mirror that same default here.
          data.supplier_type = SupplierType.External;
          // Backend requires `isChemicalSupplier` explicitly on create — this
          // form only ever creates a regular ingredient supplier (the
          // chemical-supplier flow is a separate screen, ChemicalDataRegister.tsx),
          // so it's always false here.
          data.isChemicalSupplier = false;
          // `handleSupplierSave` posts and updates `supplierData` asynchronously
          // — waiting for its own onSuccess (instead of resolving right away,
          // before the request even completes) is required both so callers
          // awaiting `performSave` don't race ahead of the real result, and so
          // the id below is the one the backend actually assigned. Card 1's
          // "Supplier Name" `FormSearchSelect` (name="supplier_search") is a
          // separate RHF field from `supplierData` — its displayed value comes
          // from whatever id is stored on that field (see `getDisplayValue` in
          // FormSearchSelect.tsx), which is still the "+ New" option's id (0)
          // at this point. Mirrors `onCreateIngredient`'s identical
          // `setSearchValue("ingredient_search", createdId)` sync above,
          // without which the field keeps showing "+ New" after the save.
          handleSupplierSave(
            data,
            savedSupplier => {
              const newSupplierId = savedSupplier?._id;
              if (newSupplierId) setSearchValue("supplier_search", String(newSupplierId));
              resolve();
            },
            err => reject(err)
          );
        })();
      });
    } else {
      await new Promise<void>((resolve, reject) => {
        handleSubmit((data: Record<string, unknown>) => {
          if (documents.length > 0) data.supportingDocuments = documents;
          saveData(data, () => resolve(), err => reject(err));
        })();
      });
    }
  }, [newSupplier, handleSubmitNewSupplier, handleSupplierSave, handleSubmit, saveData, setSearchValue]);

  // `currentDataType` state mirrors `effectiveDataType` via the protected
  // hooks' own effect (see useSupplierDataType.ts), so it's exposed to the
  // exact same hydration race `lockedDataType` guards against above —
  // reading `lockedDataType` here instead keeps these two consistent with
  // whatever the Data Type field actually displays/saves.
  const isIngredientMappingMode = lockedDataType === IngredientDataType.USER;

  const isGenericCalculationReady = IngredientRegisterButtonState.isGenericCalculationReady(
    lockedDataType,
    viewEnvironmentalData,
    validateData
  );

  const onSave = useCallback(async () => {
    try {
      await performSave();
      if (isIngredientMappingMode) {
        closeModal();
      } else if (isGenericCalculationReady) {
        setCalculateModalOpen(true);
      }
    } catch {
      errorView("An error occurred while saving the ingredient data. Please try again.");
    }
  }, [performSave, isIngredientMappingMode, isGenericCalculationReady, closeModal]);

  const onDataTypeChange = useCallback(
    (type: string) => {
      if (type === IngredientDataType.SUPPLIER && !supplierAsUser) {
        errorView("Supplier is not an user in the system");
        return;
      }
      isDataTypeUserEditRef.current = true;
      setDataType(type);
    },
    [supplierAsUser, setDataType]
  );

  const onIngredientTypeChange = useCallback(
    (type: string) => {
      isIngredientTypeUserEditRef.current = true;
      setIngredientType(type);
    },
    [setIngredientType]
  );

  const { mutate: putIngredientDataToQueue, isLoading: isLoadingPutDataToQueue } =
    usePutIngredientDataToQueue();

  const onApproveCalculate = useCallback(() => {
    putIngredientDataToQueue(
      { ingredientId: ingredientId as string, supplierId: supplierId as string },
      {
        onSuccess: () => {
          setCalculateModalOpen(false);
          refetchIngredients?.();
          setVisibleModal(false);
        },
        onError: () => setCalculateModalOpen(false)
      }
    );
  }, [putIngredientDataToQueue, ingredientId, supplierId, refetchIngredients, setVisibleModal]);

  const onNavigateIngredientMapping = useCallback(() => {
    navigation(
      `/mapping/${MappingSourceType.ingredient}?sourceId=${ingredientId}&ownerId=${supplierId}&ingredientName=${ingredientNameSelection.ingredientData?.ingredient_name}`
    );
  }, [navigation, ingredientId, supplierId, ingredientNameSelection.ingredientData]);

  return {
    // Ingredient (Card 1)
    searchControl,
    searchErrors,
    ingredientData: ingredientNameSelection.ingredientData,
    ingredientOptions: ingredientNameSelection.ingredientOptions,
    isCreatingNewIngredient: ingredientNameSelection.isCreatingNewIngredient,
    newIngredientName: ingredientNameSelection.newIngredientName,
    newIngredientError: ingredientNameSelection.newIngredientError,
    isCreatingIngredient: ingredientNameSelection.isCreatingIngredient,
    imagePath: ingredientNameSelection.imagePath,
    onSelectIngredient: ingredientNameSelection.onSelectIngredient,
    onNewIngredientNameChange: ingredientNameSelection.onNewIngredientNameChange,
    onStartCreateIngredient: ingredientNameSelection.onStartCreateIngredient,
    onCancelCreateIngredient: ingredientNameSelection.onCancelCreateIngredient,
    onCreateIngredient,
    onImageUpload: ingredientNameSelection.onImageUpload,
    onImageDelete: ingredientNameSelection.onImageDelete,

    // Supplier search (Card 1)
    supplierData: supplierData as Record<string, unknown>,
    supplierOptions,
    enableSupplierSearch: Boolean(ingredientNameSelection.ingredientData),
    onSelectSupplier,

    // Main form (Card 2 + produceCultivar in Card 3)
    register,
    control,
    errors,
    formSyncKey,
    showUnitDetails: !isEmpty(supplierData),
    defaultType,
    onDefaultTypeChange: setDefaultType,
    unitTypeOptions: IngredientRegisterOptions.unitTypes(),

    // Environmental data (Card 3)
    isEnableEnvironmentData: true,
    viewEnvironmentalData,
    onToggleEnvironmentalData: setViewEnvironmentalData,
    ingredientType: lockedIngredientType,
    onIngredientTypeChange,
    ingredientTypeOptions: IngredientRegisterOptions.ingredientTypes(),
    effectiveDataType: lockedDataType,
    onDataTypeChange,
    dataTypeOptions: IngredientRegisterOptions.dataTypes(supplierData?.supplier_type, lockedDataType),
    showRawFields: packageFlags.produceIngredient,
    showProcessedFields: packageFlags.processIngredient,
    produceType,
    onProduceTypeChange: setProduceType,
    produceTypeOptions: IngredientRegisterOptions.produceTypes(
      isProduceTypesSuccess
        ? (produceTypesData as unknown as Array<{ id: number; produce_name: string }>)
        : undefined
    ),
    isProduceTypesLoading,
    farmingType,
    onFarmingTypeChange: setFarmingType,
    farmingTypeOptions: IngredientRegisterOptions.farmingTypes(),
    fieldType,
    onFieldTypeChange: setFieldType,
    fieldTypeOptions: IngredientRegisterOptions.fieldTypes(),
    specifyIngredientValue,
    onSpecifyIngredientChange: setSpecifyIngredientValue,
    specifyIngredientOptions: IngredientRegisterOptions.specifyIngredients(specifyIngredientData),
    country,
    onCountryChange: setCountry,
    countryOptions: IngredientRegisterOptions.countries(
      isCountriesDataSuccess
        ? (countriesData as unknown as Array<{ id: number; country: string }>)
        : undefined
    ),
    isCountriesDataLoading,

    // New-supplier sub-flow
    newSupplier,
    registerNewSupplier,
    controlNewSupplier,
    errorsNewSupplier,
    handleSubmitNewSupplier,
    supplierCountry,
    onSupplierCountryChange: setSupplierCountry,
    isSupplierAsUser,
    onIsSupplierAsUserChange: setIsSupplierAsUser,
    onSubmitNewSupplier: handleSupplierSave,
    onCancelNewSupplier: closeSupplierData,

    // Mirrors SupplierRegister.tsx's blocking loader while a linked
    // supplier's saved data is still being fetched.
    isLoadingSupplierData: !isSalesUnitDataSuccess && isSupplierExist,

    // Legacy data sections (Packaging / Transportation / LCA)
    ingredientId,
    supplierId,
    enablePackageData: IngredientRegisterButtonState.enablePackageData(
      loadPackageDataFlag,
      packageFlags,
      viewEnvironmentalData
    ),
    enableTransportData: IngredientRegisterButtonState.enableTransportData(
      loadPackageDataFlag,
      packageFlags,
      viewEnvironmentalData
    ),
    enableLCAData: IngredientRegisterButtonState.enableLCAData(packageFlags, viewEnvironmentalData),
    ingredientPackageData,
    refetchPackagingData,
    lcaRef,

    // Footer / save
    hideIngredientView,
    isIngredientMappingMode,
    enableIngredientMapping: IngredientRegisterButtonState.enableIngredientMapping(
      lockedDataType,
      ingredientId,
      supplierId,
      viewEnvironmentalData
    ),
    enableIngredientButton: IngredientRegisterButtonState.enableIngredientButton(
      supplierData?.supplier_data?.dataType
    ),
    disableCalculateButton: IngredientRegisterButtonState.disableCalculateButton(
      isSupplierFormOpen,
      supplierValidation,
      currentSupplierId
    ),
    genericButtonTitle: IngredientRegisterButtonState.genericButtonTitle(isGenericCalculationReady),
    onSave,
    onNavigateIngredientMapping,
    onCancel: closeModal,

    // Calculate confirmation
    calculateModalOpen,
    onCloseCalculateModal: () => setCalculateModalOpen(false),
    onApproveCalculate,
    isLoadingPutDataToQueue
  };
};
